import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Gate a subtree behind sign-in + profile complete + T&Cs accepted + parental
// consent (under-13 only) + active subscription (or admin email).
//
//   <RequireAccess>
//     <SecretApp />
//   </RequireAccess>
//
// Flow for a fresh signed-in user:
//   1. Not signed in            -> /signin?next=<current_path>
//   2. No DOB captured yet      -> /app/age-capture
//   3. Under-13, consent pending -> /app/parental-consent-pending
//   4. T&Cs not accepted (or new version) -> /app/accept-terms
//   5. No active subscription   -> /app/paywall
//   6. All good                 -> render children
//
// Every gate screen must be reachable OUTSIDE this component so it doesn't
// bounce-loop through itself.
export function RequireAccess({ children }: { children: ReactNode }) {
  const {
    loading,
    session,
    hasAccess,
    needsAgeCapture,
    needsParentalConsent,
    needsTosAcceptance,
    isAdmin,
  } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <main className="min-h-full grid place-items-center bg-cream p-8">
        <div className="text-center">
          <div className="mx-auto w-10 h-10 rounded-full border-2 border-accent/30 border-t-accent animate-spin" />
          <p className="mt-3 text-xs text-ink/55">Checking access...</p>
        </div>
      </main>
    );
  }

  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }

  // Compliance gates run BEFORE the paywall check - a child without parental
  // consent should never see the paywall, they should see the consent-pending
  // screen. Admins skip all compliance gates.
  const at = location.pathname;
  if (!isAdmin) {
    if (needsAgeCapture && at !== '/app/age-capture') {
      return <Navigate to="/app/age-capture" replace />;
    }
    if (needsParentalConsent && at !== '/app/parental-consent-pending' && at !== '/app/age-capture') {
      return <Navigate to="/app/parental-consent-pending" replace />;
    }
    if (needsTosAcceptance && at !== '/app/accept-terms' && at !== '/app/age-capture' && at !== '/app/parental-consent-pending') {
      return <Navigate to="/app/accept-terms" replace />;
    }
  }

  if (!hasAccess) {
    // Compliance gates already handled above - if we reach here without access,
    // it's the paywall (subscription / free-uses exhausted). Avoid loop.
    const complianceGates = ['/app/age-capture', '/app/parental-consent-pending', '/app/accept-terms', '/app/paywall'];
    if (complianceGates.includes(at)) return <>{children}</>;
    return <Navigate to="/app/paywall" replace />;
  }

  return <>{children}</>;
}
